/**
 * Performance Budget Configuration
 * 
 * Defines strict performance budgets for the application.
 * These budgets should be enforced in CI/CD pipeline.
 */

export const PERFORMANCE_BUDGETS = {
  // Core Web Vitals (Google's thresholds)
  webVitals: {
    LCP: {
      good: 2500,
      poor: 4000,
      target: 2000, // Our internal target
    },
    FID: {
      good: 100,
      poor: 300,
      target: 75,
    },
    CLS: {
      good: 0.1,
      poor: 0.25,
      target: 0.05,
    },
    FCP: {
      good: 1800,
      poor: 3000,
      target: 1500,
    },
    TTFB: {
      good: 800,
      poor: 1800,
      target: 600,
    },
    INP: {
      good: 200,
      poor: 500,
      target: 150,
    },
  },

  // Bundle size budgets (gzipped)
  bundles: {
    mainJS: 250 * 1024,         // 250KB
    vendorJS: 200 * 1024,        // 200KB
    totalJS: 500 * 1024,         // 500KB
    css: 50 * 1024,              // 50KB
    fonts: 100 * 1024,           // 100KB
    images: 500 * 1024,          // 500KB per page
    total: 1000 * 1024,          // 1MB total initial load
  },

  // Resource counts
  resources: {
    maxScripts: 10,
    maxStylesheets: 5,
    maxFonts: 3,
    maxImages: 20,
  },

  // Lighthouse scores (out of 100)
  lighthouse: {
    performance: 90,
    accessibility: 95,
    bestPractices: 95,
    seo: 95,
    pwa: 80,
  },

  // Custom metrics
  custom: {
    assessmentLoadTime: 1000,    // Assessment page should load in 1s
    dashboardLoadTime: 1500,     // Dashboard should load in 1.5s
    apiResponseTime: 500,        // API calls should respond in 500ms
    databaseQueryTime: 100,      // DB queries should complete in 100ms
  },
};

/**
 * Check if a metric exceeds its budget
 */
export function checkBudget(
  metricName: string,
  value: number,
  budgetType: 'webVitals' | 'bundles' | 'resources' | 'lighthouse' | 'custom'
): {
  passed: boolean;
  budget: number;
  value: number;
  percentageOfBudget: number;
  severity: 'good' | 'warning' | 'critical';
} {
  const budgets = PERFORMANCE_BUDGETS[budgetType];
  const budget = budgets[metricName as keyof typeof budgets] as number;

  if (typeof budget !== 'number') {
    return {
      passed: true,
      budget: 0,
      value,
      percentageOfBudget: 0,
      severity: 'good',
    };
  }

  const percentageOfBudget = (value / budget) * 100;
  const passed = value <= budget;

  let severity: 'good' | 'warning' | 'critical';
  if (percentageOfBudget <= 80) {
    severity = 'good';
  } else if (percentageOfBudget <= 100) {
    severity = 'warning';
  } else {
    severity = 'critical';
  }

  return {
    passed,
    budget,
    value,
    percentageOfBudget,
    severity,
  };
}

/**
 * Format bytes to human-readable size
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * Generate performance report
 */
export function generatePerformanceReport(metrics: Record<string, number>): string {
  let report = '# Performance Budget Report\n\n';

  Object.entries(metrics).forEach(([name, value]) => {
    const check = checkBudget(name, value, 'webVitals');
    const icon = check.passed ? '✅' : '❌';
    report += `${icon} **${name}**: ${value}ms (Budget: ${check.budget}ms)\n`;
  });

  return report;
}
